import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Atom, Stethoscope, Loader2, ArrowRight } from "lucide-react";
import { api, homeForRole } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const Card = ({ active, onClick, icon: Icon, title, desc, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={cn(
      "flex w-full items-start gap-4 rounded-2xl border p-5 text-left transition-colors",
      active ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
    )}
  >
    <span className={cn("flex h-11 w-11 items-center justify-center rounded-xl", active ? "bg-primary text-primary-foreground" : "bg-secondary text-primary")}>
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <div className="font-heading text-lg font-semibold">{title}</div>
      <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
    </div>
  </button>
);

export default function Onboarding() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [exam, setExam] = useState("");
  const [level, setLevel] = useState("");
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);

  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => thisYear + i);
  const canSubmit = year && (exam === "NEET" || (exam === "JEE" && level));

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.put("/auth/onboarding", { examPreference: exam, jeeLevel: exam === "JEE" ? level : null, examYear: Number(year) });
      setUser(data);
      navigate(homeForRole(data.role), { replace: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-lg px-4 py-14">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="onboarding-page">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Quick setup</p>
        <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Which exam are you preparing for?</h1>
        <p className="mt-2 text-muted-foreground">We'll tailor your practice feed to it. You can change this later.</p>

        <div className="mt-8 space-y-3">
          <Card active={exam === "JEE"} onClick={() => setExam("JEE")} icon={Atom} title="JEE" desc="Physics, Chemistry & Mathematics" testid="onboarding-exam-jee" />
          <Card active={exam === "NEET"} onClick={() => { setExam("NEET"); setLevel(""); }} icon={Stethoscope} title="NEET" desc="Physics, Chemistry, Botany & Zoology" testid="onboarding-exam-neet" />
        </div>

        {exam === "JEE" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6">
            <p className="mb-3 text-sm font-medium">JEE level</p>
            <div className="flex gap-3">
              {["MAIN", "ADVANCED"].map((l) => (
                <button
                  key={l}
                  data-testid={`onboarding-level-${l}`}
                  onClick={() => setLevel(l)}
                  className={cn(
                    "flex-1 rounded-xl border px-4 py-3 text-sm font-medium transition-colors",
                    level === l ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40"
                  )}
                >
                  {l === "MAIN" ? "JEE Main" : "JEE Advanced"}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {exam && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6">
            <p className="mb-3 text-sm font-medium">Which year will you appear for the exam?</p>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
              {years.map((y) => (
                <button
                  key={y}
                  type="button"
                  data-testid={`onboarding-year-${y}`}
                  onClick={() => setYear(String(y))}
                  className={cn(
                    "rounded-xl border px-3 py-3 text-sm font-medium transition-colors",
                    year === String(y) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40"
                  )}
                >
                  {y}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        <Button onClick={submit} disabled={!canSubmit || loading} data-testid="onboarding-submit" className="mt-8 w-full rounded-xl py-6 text-base">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Start practising <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </motion.div>
    </div>
  );
}
